import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../utils/authService';
import { getSafeRedirectTarget } from '../../utils/authRedirect';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SEO from '../../components/SEO';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect if user is already logged in (to redirect target if present)
  useEffect(() => {
    if (!authLoading && user) {
      const redirectParam = searchParams.get('redirect');
      const target = getSafeRedirectTarget(redirectParam);
      if (target === 'payment') {
        navigate('/pricing');
      } else {
        navigate(target);
      }
    }
  }, [user, authLoading, navigate, searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signIn(formData.email, formData.password);
      
      if (result.success) {
        const redirectParam = searchParams.get('redirect');
        const paymentIntent = localStorage.getItem('paymentIntent');
        const target = getSafeRedirectTarget(redirectParam);
        if (target === 'payment' && paymentIntent) {
          navigate('/pricing');
        } else if (target !== 'payment') {
          navigate(target);
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await authService.signInWithGoogle();
      if (result.success) {
        const redirectParam = searchParams.get('redirect');
        const paymentIntent = localStorage.getItem('paymentIntent');
        const target = getSafeRedirectTarget(redirectParam);
        if (target === 'payment' && paymentIntent) {
          navigate('/pricing');
        } else if (target !== 'payment') {
          navigate(target);
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Google sign in failed');
      }
    } catch (error) {
      setError('Google sign in failed');
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      const result = await authService.signInWithFacebook();
      if (result.success) {
        const redirectParam = searchParams.get('redirect');
        const paymentIntent = localStorage.getItem('paymentIntent');
        const target = getSafeRedirectTarget(redirectParam);
        if (target === 'payment' && paymentIntent) {
          navigate('/pricing');
        } else if (target !== 'payment') {
          navigate(target);
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Facebook sign in failed');
      }
    } catch (error) {
      setError('Facebook sign in failed');
    }
  };

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Login"
        description="Sign in to your EmailValidator account to access email validation services, manage your credits, and view validation history."
        keywords="login, sign in, email validator login, user account, email validation account, user authentication"
        canonical="https://mailvalidator.octalchip.com/auth/login"
      />
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to={searchParams.get('redirect') ? `/auth/register?redirect=${encodeURIComponent(searchParams.get('redirect'))}` : '/auth/register'}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              create a new account
            </Link>
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <Link
                to="/contact"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Forgot your password? Contact support
              </Link>
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              loading={loading}
              animation="glow"
              className="w-full"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleSignIn}
                animation="float"
                className="w-full"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Google
              </Button>

              {/* Facebook button temporarily hidden */}
              {/* <Button
                type="button"
                variant="outline"
                onClick={handleFacebookSignIn}
                className="w-full"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </Button> */}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;